import {PayloadAction, createEntityAdapter, createSlice} from '@reduxjs/toolkit';
import {makeWorkspaceQueryKey} from 'src/modules/profile/user/tabs/content/workspace/util';
import {
    getUserWorkspacesFailure,
    getUserWorkspacesRequest,
    getUserWorkspacesSuccess,
} from 'src/openapi/admin/apis/UserWorkspacesApi';
import {CollaboratorRole} from 'src/openapi/admin/models/CollaboratorRole';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {WorkspaceGeneralInfo} from 'src/openapi/admin/models/WorkspaceGeneralInfo';
import {WorkspaceRequest, WorkspaceRequestSortBy} from 'src/openapi/admin/models/WorkspaceRequest';

const workspacesAdapter = createEntityAdapter<WorkspaceGeneralInfo, number>({
    selectId: ws => ws.workspaceId,
});

type QueryState = {
    ids: number[],
    total: number,
    status: 'idle' | 'loading' | 'succeeded' | 'failed',
    error?: string,
    receivedAt?: number,
    requestId?: string,
};

export type UserPageState = {
    userPage: {
        tabs: {
            content: {
                workspaces: WorkspacesSliceState,
            },
        },
    },
};


type WorkspacesSliceState = {
    queries: Record<string, QueryState>,
    criteriaByScope: Record<string, WorkspaceRequest>,
} & ReturnType<typeof workspacesAdapter.getInitialState>;

const initialState: WorkspacesSliceState = {
    ...workspacesAdapter.getInitialState(),
    queries: {},
    criteriaByScope: {},
};

export const defaultWorkspaceCriteria: WorkspaceRequest = {
    pagination: {currentPage: 1, pageSize: 15},
    sortBy: WorkspaceRequestSortBy.LAST_MODIFIED_DATE,
    sortDirection: SortDirection.DESC,
    ownerFilter: [CollaboratorRole.OWNER, CollaboratorRole.EDITOR, CollaboratorRole.VIEWER],
};

export const workspacesSlice = createSlice({
    name: 'workspaces',
    initialState,
    reducers: {
        setScopeCriteria: (
            state,
            action: PayloadAction<{scopeKey: string, patch: Partial<WorkspaceRequest>}>,
        ) => {
            const {scopeKey, patch} = action.payload;
            const prev = state.criteriaByScope[scopeKey] || defaultWorkspaceCriteria;

            const resetPageNeeded =
                patch.nameQuery !== undefined ||
                patch.ownerFilter !== undefined;

            state.criteriaByScope[scopeKey] = {
                ...prev,
                ...patch,
                pagination: {
                    ...prev.pagination,
                    ...(patch.pagination || {}),
                    currentPage: resetPageNeeded
                        ? 1
                        : (patch.pagination?.currentPage ?? prev.pagination?.currentPage ?? 1),
                },
            };
        },
        resetScopeCriteria: (state, action: PayloadAction<string>) => {
            state.criteriaByScope[action.payload] = defaultWorkspaceCriteria;
        },
        invalidateQuery: (state, action: PayloadAction<string>) => {
            const query = state.queries[action.payload];
            if (query) {
                query.receivedAt = undefined;
            }
        },
        invalidateQueriesForUser: (state, action: PayloadAction<number>) => {
            const prefix = `${action.payload}::`;
            Object.keys(state.queries).forEach(key => {
                if (key.startsWith(prefix)) {
                    state.queries[key].status = 'idle';
                    state.queries[key].receivedAt = undefined;
                }
            });
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getUserWorkspacesRequest, (state, action) => {
                const {userId, workspaceRequest} = action.payload.meta;
                const key = makeWorkspaceQueryKey(userId, workspaceRequest);
                state.queries[key] = {
                    ids: [],
                    total: 0,
                    status: 'loading',
                };
            })
            .addCase(getUserWorkspacesSuccess, (state, action) => {
                const {userId, workspaceRequest} = action.payload.meta;
                const {workspaces, total} = action.payload.data;
                const key = makeWorkspaceQueryKey(userId, workspaceRequest);

                workspacesAdapter.upsertMany(state, workspaces);

                state.queries[key] = {
                    ids: workspaces.map(w => w.workspaceId),
                    total,
                    status: 'succeeded',
                    receivedAt: Date.now(),
                };
            })
            .addCase(getUserWorkspacesFailure, (state, action) => {
                const {userId, workspaceRequest} = action.payload.meta;
                const key = makeWorkspaceQueryKey(userId, workspaceRequest);

                state.queries[key] = {
                    ids: [],
                    total: 0,
                    status: 'failed',
                    error: action.payload.data?.message ?? 'Unknown error',
                };
            });
    },
});

export const {
    setScopeCriteria,
    resetScopeCriteria,
    invalidateQuery,
    invalidateQueriesForUser,
} = workspacesSlice.actions;

export const workspacesSelectors = workspacesAdapter.getSelectors<{
    userPage: {tabs: {content: {workspaces: WorkspacesSliceState}}},
}>(state => state.userPage.tabs.content.workspaces);
